import { useFieldArray, useForm, useWatch } from "react-hook-form";
import {
  DatePicker,
  Input,
  MultipleSelect,
  Select,
  Submit,
  TextArea,
} from "../FormComponents";
import {
  buildTreatmentsDataArray,
  initializeDrugBatches,
  initializeProtocolId,
} from "../../services/common";
import useLabels from "../../hooks/useLabels";
import { add, empty, setOperations, updateOp } from "../../context/operations/operationsSlice";
import { useNavigate, useParams } from "react-router";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useDispatch, useSelector } from "react-redux";
import {
  getDrugs,
  getOperations,
  getProtocols,
  getUnits,
  getWarehouseByType,
  newOperation,
  updateOperation,
} from "../../services/utils";
import { BottleIcon, ConfirmationModal, CTA, DestinationSelector, DrugsBatchSelectorTable, OperationRecap, ProtocolConflictModal, SourceSelector, TankIcon } from "../Generic";
import {
  ALPHA_NUM_SPACE_DASH_UNDERSCORE_REGEX,
  INT_REGEX,
} from "../../constants/regex";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { selectAllLotNames, selectDrugs, selectOperations, selectUnits, selectWarehouseByType, selectWineMaking } from "../../context/selectors";
import { DestTankDetail } from "../OperationsDetail/Details";
import { NavLink } from "react-router-dom";
import { setWarehouse } from "../../context/warehouse/warehouseSlice";
import TableEdit from "../TableEdit";
import { setWineMaking } from "../../context/protocols/wineMakingSlice";
import { createColumnHelper } from "@tanstack/react-table";
import TableCell from "../TableEdit/TableCell";
import EditCell from "../TableEdit/EditCell";
import { setDrugs } from "../../context/substances/drugsSlice";
import { setUnits } from "../../context/units/unitsSlice";
import { toastAxios } from "../../services/toastAxios";
import {
  getInputTankUnit,
  inputToLiters,
  litersToInput,
} from "../../services/tankUnit";

const BottlingForm = forwardRef(({
  operation,
  lots,
  activeWinery,
  tanks,
  users,
  configs,
  type,
  isDuplicate = false,
  isMobile,
  onConfigSave,
  forcedSrcTank,
}, formRef) => {
  const { id } = useParams();
  const [getLabel] = useLabels();
  const inputUnit = getInputTankUnit();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isConflictOpen, setIsConflictOpen] = useState(false);
  const [conflictProtocols, setConflictProtocols] = useState([]);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [isExactDate, setIsExactDate] = useState(false);
  const operations = useSelector(selectOperations);
  const protocols = useSelector(selectWineMaking);
  const drugs = useSelector(selectDrugs);
  const units = useSelector(selectUnits);
  const lotNames = useSelector(selectAllLotNames);
  const capsData = useSelector(selectWarehouseByType("caps"));
  const bottlesData = useSelector(selectWarehouseByType("bottles"));
  const machinesData = useSelector(selectWarehouseByType("machineries"));
  const [isBatchSelectorModal, setIsBatchSelectorModal] = useState("");
  
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setError,
    clearErrors,
    reset,
    setValue,
    getValues,
    control,
    trigger,
  } = useForm({
    defaultValues: {
      dest_tanks: operation?.dest_tanks || [
        { bottle_lot: { label: "", value: ""}, cap_lot: { label: "", value: ""} },
      ],
      priority: operation?.priority || "",
      expire_date: operation?.expire_date || null,
      type: { type },
      tank_id_in: operation?.tank_id_in || "",
      cellarman_ids: operation?.cellarman_ids || "",
    },
    mode: "onTouched",
  });
 
  const { dest_tanks, tank_id_in, machinery_id, protocol_id, drug_batches, subtype } = useWatch({ control });

  useImperativeHandle(formRef, () => ({
    submitForm: async () => {
      const isFormValid = await trigger();
      if (!isFormValid) return;
      handleSubmit(() => {
        if (onConfigSave) {
          sendData();
        } else {
          openFormModal();
        }
      })();
    },
  }), [handleSubmit, onConfigSave, trigger]);

  const [totalTreatmentVolume, setTotalTreatmentVolume] = useState(0);

  const drugBatchFieldArray = useFieldArray({
    control,
    name: "drug_batches",
  });

  const { fields, append, remove, replace, update } =
    useFieldArray({
      control,
      name: "dest_tanks",
    });

    // const { dests } = useWatch({ control });

  // const { fields, append, prepend, remove, swap, move, insert, replace } =
  //   useFieldArray({
  //     control,
  //     name: "dests",
  //   });
  
  const loadOperations = async () => {
    const loadedOperations = await getOperations(axiosPrivate);
    dispatch(setOperations(loadedOperations));
  };

  const loadWarehouse = async (t) => {
    const currentData = await getWarehouseByType(axiosPrivate, t);
    dispatch(setWarehouse({ type: t, data: currentData }));
  };

  const loadProtocols = async () => {
    const loadedProtocols = await getProtocols("winemaking", axiosPrivate);
    dispatch(setWineMaking(loadedProtocols));
  };

  const loadDrugs = async () => {
    const currentDrugs = await getDrugs(axiosPrivate);
    dispatch(setDrugs(currentDrugs));
  };

  const loadUnits = async () => {
    const currentUnits = await getUnits(axiosPrivate);
    dispatch(setUnits(currentUnits));
  };

  const columnHelper = createColumnHelper();
  const columns = [
    // columnHelper.accessor((row) => row?.day + (row?.day_to ? ` - ${row?.day_to}` : ""), {
    //   header: getLabel("protocolDay"),
    //   cell: TableCell,
    //   meta: {
    //     type: "number",
    //     register: register,
    //   },
    // }),
    // columnHelper.accessor(
    //   (row) => (row?.phase ? getLabel(`protocolPhase_${row.phase}`) : ""),
    //   {
    //     header: getLabel("protocolPhase"),
    //     cell: TableCell,
    //   }
    // ),
    columnHelper.accessor(
      (row) =>
        row?.functional
          ? getLabel(`drugLabelGroup_${row?.functional}`)
          : "",
      {
        header: getLabel("drugType"),
        cell: TableCell,
        meta: {
          type: "text",
          isFilterDisabled: true,
          filterVariant: "text",
        },
      }
    ),
    columnHelper.accessor((row) => row?.product_name || "", {
      header: getLabel("protocolProduct"),
      cell: TableCell,
      meta: {
        type: "text",
        // register: register,
        isFilterDisabled: true,
      },
    }),
    columnHelper.accessor(
      (row) =>
        row?.quantity
          ? row?.protocolType === "HOMOGENIZATION_TEMP" ? `${row?.quantity} \u00b0C` : (row?.quantity || 0) +
            " " +
            (row?.product?.unit || row?.product_unit || "")
            + (row?.protocolType === 'MICRO' ? 
                getLabel('protocolOxygenationMonth') : row?.protocolType === 'MACRO' ? 
                getLabel('protocolOxygenationDay') : row?.protocolType === 'CUSTOM' ?
                `/${row?.oxygenation_dose_hours}h:${row?.oxygenation_dose_minutes}m` : '')
          : "",
      {
        header: getLabel("protocolQuantity"),
        cell: TableCell,
        meta: {
          type: "number",
          register: register,
          isFilterDisabled: true,
        },
      }
    ),
    // columnHelper.accessor(
    //   (row) =>
    //     row?.protocolType ? getLabel(`protocolType_${row?.protocolType}`) : "",
    //   {
    //     header: getLabel("protocolType"),
    //     cell: TableCell,
    //     meta: {
    //       type: "text",
    //       register: register,
    //       // placeholder: getLabel("lotColorPlaceholder"),
    //     },
    //   }
    // ),
    columnHelper.display({
      id: "edit",
      cell: EditCell,
      meta: {
        isEditable: true,
      },
    }),
  ];

  const cipColumns = [
    columnHelper.accessor(
      (row) => row?.day + (row?.day_to ? ` - ${row?.day_to}` : ""),
      {
        header: getLabel("protocolStep"),
        cell: TableCell,
        meta: {
          type: "number",
          isFilterDisabled: true,
          // register: modalRegister,
        },
      }
    ),
    columnHelper.accessor(
      (row) =>
        row?.operation ?
          row.operation === 'ADD' ? computeAddOpTypeString(row) : 
            getLabel(`protocolOperation_${row.operation}`) : "",
      {
        header: getLabel("protocolOperation"),
        cell: TableCell,
        meta: {
          type: "text",
          isFilterDisabled: true,
          // register: modalRegister,
        },
      }
    ),
    columnHelper.accessor(
      (row) =>
        row?.operation === "SO2"
          ? ""
          : (row.operation === "RINSE" || row.operation === "VAPORIZATION")
          ? getLabel("protocolProduct_H2O")
          : row?.product?.name || row?.product_name || "",
      {
        header: getLabel("protocolProduct"),
        cell: TableCell,
        meta: {
          type: "text",
          isFilterDisabled: true,
          // register: modalRegister,
        },
      }
    ),
    columnHelper.accessor(
          (row) =>
            row?.quantity
              ? (row.operation === "RINSE" || row.operation === "VAPORIZATION") ? ""
                : row?.operation === "CLEANING" ? `${row?.quantity} %` : "" 
              : "",
          {
            header: getLabel("protocolConcentration"),
            cell: TableCell,
            meta: {
              type: "number",
            isFilterDisabled: true,
            },
          }
        ),
      columnHelper.accessor(
        (row) =>
          row?.work_time
            ? `${row?.work_time} min`
            : "",
        {
          header: getLabel("protocolHomogenizationContactTime"),
          cell: TableCell,
          meta: {
            type: "text",
            isFilterDisabled: true,
          },
        }
      ),
    // columnHelper.display({
    //   id: "edit",
    //   cell: EditCell,
    //   meta: {
    //     isEditable: true,
    //   },
    // })
  ];

  useEffect(() => {
    const totalVolume = dest_tanks?.reduce((acc, curr) => {
      return acc + inputToLiters(curr?.quantity, inputUnit);
    }, 0);
    setTotalTreatmentVolume(totalVolume);
  }, [dest_tanks]);

  // Load reference data once on mount
  useEffect(() => {
    if (!units || units.length === 0) loadUnits();
    if (!drugs || drugs?.length === 0) loadDrugs();
    if (!protocols || protocols?.length === 0) loadProtocols();
    if (!capsData || capsData.length === 0) loadWarehouse("caps");
    if (!bottlesData || bottlesData.length === 0) loadWarehouse("bottles");
    if (!machinesData || machinesData.length === 0) loadWarehouse("machineries");
    if (id && (!operations || operations?.length === 0)) loadOperations();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize form values when data is ready
  useEffect(() => {
    if (id && operations?.length > 0) {
      const operation = operations?.find((o) => o.id === Number(id)) || null;
      if (operation === null) return;

      const tank = tanks?.find(
        (t) => t.id === operation?.src_tanks[0]?.tank_id
      );
      setValue("tank_id_in", {
        value: tank?.id,
        label: tank?.name,
        color: tank?.color,
        quantity: tank?.quantity,
        capacity: tank?.capacity,
        state: tank?.state,
        lot: tank?.batch_name,
        batch_type: tank?.batch_type,
      });

      setValue("priority", {
        label: getLabel(`operationPriority${operation?.priority}`),
        value: operation?.priority,
      });

      if (operation?.winemaking_protocol_id) {
          initializeProtocolId(type, protocols, operation?.winemaking_protocol_id, setValue);
          initializeDrugBatches(operation, drugs, setValue);
        
        // const protocol = protocols?.find((p) => p?.protocol_type === 'POST' && p?.id === operation?.winemaking_protocol_id) || null;
        // if (protocol) {
        //   setValue("protocol_id", {
        //     label: protocol?.name,
        //     value: protocol?.id,
        //   });
        // }
      }
      if (operation?.cleaning_protocol_id) {
        const protocol = protocols?.find((p) => p?.protocol_type === 'CIP' && p?.id === operation?.cleaning_protocol_id) || null;
        if (protocol) {
          setValue("cleaning_protocol_id", {
            label: protocol?.name,
            value: protocol?.id,
          });
        }
      }

      if (operation?.machinery_id) {
        const machine = machinesData?.find((m) => m?.id === operation?.machinery_id) || null;
        if (machine) {
          setValue("machinery_id", {
            label: machine?.name,
            value: machine?.id,
          });
        }
      }

      if (operation?.subtype) {
        setValue("subtype", {
          value: operation?.subtype,
          label: getLabel(`operationSubtype${operation?.subtype}`),
        });
      }

      const dests = [];
      operation?.dest_tanks?.forEach((s, i) => {
        const batch_name = s?.batch_name;

        let is_new = s?.is_new === true;
        if (is_new) {
          is_new = (lots
              ?.find(
                (l) => l?.name === batch_name 
                && l?.status !== "PLANNED" 
                && l?.state === "BOTTLED"
                && l?.name?.toLowerCase?.()
              ) === undefined);
          
          if (is_new) {
            // NOW detect “duplicate new” earlier in this same op
            const alreadyUsed = operation?.dest_tanks
              .slice(0, i)   // only look at earlier entries
              .some(prev => {
                // whichever field actually holds the “new” name in your data
                const prevName = prev?.is_planned === true 
                  ? prev?.planned_batch_name 
                  : prev?.batch_name;
                return prev?.is_new && prevName === batch_name;
              });
      
            if (alreadyUsed) {
              is_new = false;
            }
          }
        }

          dests.push({
            batch_name: batch_name,
            batch_id: s?.batch_id,
            batch_type: s?.batch_type,
            cap_lot: { label: s?.cap_lot, value: s?.cap_id, name: `${s?.cap_lot} (x${s?.quantity || 0})` },
            bottle_lot: { bottle_type: s?.bottle_type, label: s?.bottle_lot, value: s?.bottle_id, name: `${s?.bottle_lot} (x${s?.quantity || 0})` },
            quantity: litersToInput(s?.quantity, inputUnit),
            state: s?.state,
            color: s?.color,
            new_batch_name: is_new ? s?.batch_name : "",
            batch_name_selector: {
              value: is_new,
              label: is_new ? getLabel("lotNewName") : s?.batch_name,
            },
            // batch_name_selector:
            //   is_new
            //     ? {
            //         value: true,
            //         label: getLabel("lotNewName"),
            //       }
            //     : {
            //         value: false,
            //         label: s?.batch_name,
            //       },
          });
      });
      replace(dests);
      const cellarmans = [];
      operation?.cellarman_ids?.map((u) => {
        const user = users?.find((us) => us.id === u);
        cellarmans.push({
          value: u,
          label: user?.username,
        });
      });

      setValue("cellarman_ids", cellarmans);

      setValue("expire_date", operation?.expire_date);

      setValue("note", operation?.note);
    } else if (!id && onConfigSave && operation?.dest_tanks?.length > 0) {
      // Wizard back-navigation: restore previously saved config
      const restoredDests = operation.dest_tanks.map((s) => {
        const isNew = s.new_batch_type !== "OLD";
        const bottle = bottlesData?.find((b) => b.id === s.bottle_id);
        const cap = capsData?.find((c) => c.id === s.cap_id);
        return {
          batch_name_selector: {
            value: isNew ? true : null,
            label: s.new_batch_name || "",
          },
          new_batch_name: isNew ? (s.new_batch_name || "") : "",
          quantity: s.quantity,
          bottle_lot: bottle
            ? { value: bottle.id, label: bottle.name, quantity: bottle.quantity }
            : { label: "", value: "" },
          cap_lot: cap
            ? { value: cap.id, label: cap.name, quantity: cap.quantity }
            : { label: "", value: "" },
        };
      });
      replace(restoredDests);

      const params = operation.operation_params || {};
      if (params.winemaking_protocol_id) {
        initializeProtocolId(type, protocols, params.winemaking_protocol_id, setValue);
      }
      if (params.cleaning_protocol_id) {
        const cipProtocol = protocols?.find(
          (p) => p?.protocol_type === "CIP" && p?.id === params.cleaning_protocol_id
        );
        if (cipProtocol) {
          setValue("cleaning_protocol_id", { label: cipProtocol.name, value: cipProtocol.id });
        }
      }
      if (params.machinery_id) {
        const machine = machinesData?.find((m) => m.id === params.machinery_id);
        if (machine) {
          setValue("machinery_id", { label: machine.name, value: machine.id });
        }
      }
      if (params.subtype) {
        setValue("subtype", {
          value: params.subtype,
          label: getLabel(`operationSubtype${params.subtype}`),
        });
      }
    }
  }, [operations, operation, lots, machinesData, bottlesData, capsData]);

  // Pre-fill and lock source tank in multi-move wizard mode
  useEffect(() => {
    if (!forcedSrcTank) return;
    setValue("tank_id_in", {
      value: forcedSrcTank.id,
      label: forcedSrcTank.name,
      color: forcedSrcTank.color,
      type: forcedSrcTank.type,
      state: forcedSrcTank.state,
      quantity: forcedSrcTank.quantity,
      capacity: forcedSrcTank.capacity,
      lot: forcedSrcTank.batch_name,
      lot_id: forcedSrcTank.batch_id,
      batch_type: forcedSrcTank.batch_type,
      planned_quantity: forcedSrcTank.quantity,
      is_planned: false,
    });
  }, [forcedSrcTank, setValue]);

  const customIdNewOp = "custom-id-new-op";
  const customIdUpdateOp = "custom-id-update-op";
  const sendData = async () => {
    const data = getValues();
    const dests = [];
    const newBatchIds = [];

    const bottledLots = lots?.filter(l => l?.state === 'BOTTLED') || [];
    // console.log('bottledLots', bottledLots)

    try {
      data?.dest_tanks
        ?.filter(
          (d) =>
            d?.quantity
        )
        ?.forEach((destT, index) => {
          const tankData = destT?.tankData;
          const isPlanned =
            tankData?.value &&
            tankData?.is_planned_by_other_ops &&
            Number(tankData?.quantity) === 0 &&
            tankData?.is_planned;
    
          const new_batch_name = isPlanned
            ? tankData?.planned_batch_name
            : destT?.new_batch_name;
    
          const batch_name_label = destT?.batch_name_selector?.label;
          const batch_name_value = destT?.batch_name_selector?.value;
          // console.log('batch_name_label', batch_name_label, 'batch_name_value', batch_name_value, 'destT?.new_batch_name', destT?.new_batch_name)

          const new_name = (
            batch_name_value || isPlanned ? new_batch_name : batch_name_label
          )?.trim();
          
          const isPlannedByOtherOps = tankData?.is_planned && (
            (id === null && tankData?.programmed_as_dest?.length > 0) ||
            (id !== null && tankData?.programmed_as_dest?.some(p_id => p_id !== Number(id))));
          
          // if (tankData?.value === 89) console.log(' id && tankData?.is_planned', (tankData?.is_planned && tankData?.planned_batch_name) ? true : false)
    
          const l =
            bottledLots?.find((l) => l?.name === new_name) || null;
          
          const destQuantityLiters = inputToLiters(destT.quantity, inputUnit);
          const dest = {
            quantity: destQuantityLiters,
            new_batch_type: l ? "OLD" : "NEW",
            new_batch_name: (batch_name_value === true
              ? new_batch_name
              : batch_name_label
            )?.trim() || null,
            bottle_id: destT?.bottle_lot?.value,
            cap_id: destT?.cap_lot?.value,
            old_batch_id:
              l?.batch_id ||
              "",
            old_quantity:
              l?.quantity ||
              0,
            new_quantity:
              (Number(
                l?.quantity ||
                  0
              ) || 0) + (destQuantityLiters || 0),
            batch_type: tank_id_in?.batch_type,
          };

          if (dest.new_batch_name === null) {
            dest.new_batch_name = l?.planned_batch_name || '';
          }
          dests.push(dest);
          // console.log('dest', dest)
        });
    } catch (err) {
      toast.error(getLabel("toast_invalidDestTanks"), {
        toastId: customIdInvalidLotName,
        position: toast.POSITION.BOTTOM_RIGHT,
        exclude: true,
        autoClose: 5000,
      });
      return;
    }
    // console.log('dests', dests)
    // return
    if (dest_tanks?.length === 0 || data?.dest_tanks?.length !== dest_tanks?.length) {
      toast.error(
        getLabel(
          dest_tanks?.length === 0
            ? "toast_missingDestTanks"
            : "toast_invalidDestTanks"
        ),
        {
          toastId: customIdInvalidLotName,
          position: toast.POSITION.BOTTOM_RIGHT,
          exclude: true,
          autoClose: 5000,
        }
      );
      return;
    }
    const tmpDate = new Date(data?.expire_date)?.setHours(23, 59, 59);
    // console.log('dests', dests)
    // return;
    const requestPayload = {
      dest_tanks: dests,
      priority: data?.priority.value,
      is_exact_date: isExactDate,
      expire_date: new Date(tmpDate)?.toISOString(),
      type: type,
      subtype: data?.subtype?.value,
      winery_id: activeWinery?.id,
      cellarman_ids: data?.cellarman_ids?.map((cellMan) => cellMan?.value),
      // new_batch_name: data?.new_batch_name,
      src_tanks: [
        {
          tank_id: data?.tank_id_in?.value,
          batch_id: data?.tank_id_in?.lot_id,
          old_quantity: data?.tank_id_in?.quantity,
          new_quantity: Number(data?.tank_id_in?.quantity) - getTotalQuantity(),
        },
      ],
      note: data?.note,
      machinery_id: data?.machinery_id?.value,
      winemaking_protocol_id: data?.protocol_id?.value || null,
      cleaning_protocol_id: data?.cleaning_protocol_id?.value || null,
    };
    const tmp_treatments_data = [];

    const chosenProtocols = [];
    if (data?.protocol_id?.value) chosenProtocols.push({ id: data?.protocol_id?.value, type: data?.protocol_id?.protocol_type });
    if (data?.cleaning_protocol_id?.value) chosenProtocols.push({ id: data?.cleaning_protocol_id?.value, type: data?.cleaning_protocol_id?.protocol_type });
    
    // for (const p of chosenProtocols) {
    //   const protocol = protocols?.find((pp) => pp?.type === p?.type && pp?.id === p?.id) || null;
    //     if (protocol) {
    //       buildTreatments(data, protocol, tmp_treatments_data);
    //     // const getSubstanceById = (id) => drugs?.find((s) => s?.id === Number(id));
    //     // // Build from protocol operations
    //     // for (const step of (protocol?.operations || [])) {
    //     //   const tmp = {
    //     //     id: Number(step.id),
    //     //     day: Number(step.day),
    //     //     phase: step.phase,
    //     //     operation: step.operation,
    //     //     goal: step.goal,
    //     //     note: step.note,
    //     //     protocol_id: protocol?.id,
    //     //     info: {
    //     //       analysisType: data?.analysisType?.value || "tank",
    //     //     },
    //     //     // ...status,
    //     //   };

    //     //   const substance = getSubstanceById(step?.product);
    //     //   if (!substance) continue;

    //     //   const baseQuantity = Number(step.quantity) || 0;

    //     //   // Override quantity from data.column if present
    //     //   const override = Object.values(data.column || {}).find(
    //     //     (col) => Number(col.substance_id) === substance.id
    //     //   );
    //     //   const quantity =
    //     //     override && Number(override.new) >= 0
    //     //       ? Number(override.new)
    //     //       : baseQuantity;

    //     //   // const status = substanceStatusMap[substance.id] || { removed: false, added: false };
    //     //   // tmp.operation = "ADD";
    //     //   tmp.product = substance?.id;
    //     //   tmp.substance_name = substance?.name;
    //     //   tmp.quantity = Number(quantity);
    //     //   tmp.unit_id = substance.unit_id || substance?.unit_id;
    //     //   tmp.unit = substance.unit || substance?.unit;
    //     //   tmp.protocol_type =
    //     //     step.operation === "ADD" ? step?.functional : step.operation;
  
    //     //   tmp_treatments_data.push(tmp);
    //     // }
    //   }
    // }
    
    const analysisType = {
      value: "tank"
    };
    
    const treatments_data = buildTreatmentsDataArray(drugs, drug_batches, data, winemakingBottlingTreatments, data?.protocol_id, "TREATMENT", id, operation, operations, analysisType);

    requestPayload.treatments_data = treatments_data?.map(t => { return { ...t, phase: 'BOTTLING' }});

    console.log("requestPayload", requestPayload);

    if (onConfigSave) {
      onConfigSave({
        src_tanks: requestPayload.src_tanks,
        dest_tanks: requestPayload.dest_tanks,
        treatments_data: requestPayload.treatments_data || [],
        operation_params: {
          type: requestPayload.type,
          subtype: requestPayload.subtype,
          machinery_id: requestPayload.machinery_id,
          winemaking_protocol_id: requestPayload.winemaking_protocol_id,
          cleaning_protocol_id: requestPayload.cleaning_protocol_id,
        },
      });
      return;
    }

    // return;
    let response = null;

    if (id && !isDuplicate) {
      response = await toastAxios(
        () => updateOperation(id, requestPayload, axiosPrivate),
        customIdUpdateOp,
        { name: operation?.type || "" },
        getLabel,
      );
    } else {
      response = await toastAxios(
        () => newOperation(requestPayload, axiosPrivate),
        customIdNewOp,
        { name: operation?.type || "" },
        getLabel,
      );
      if (response?.requires_confirmation === true) {
        setPendingPayload(requestPayload);
        setConflictProtocols(response.affected_protocols || []);
        setIsConflictOpen(true);
        return;
      }
    }

    if (response && response?.success) {
      const updated_op = response?.updated_data || null;
      updateStore(updated_op);
      navClickHandler();
    }
  };

  const handleConflictConfirm = async () => {
    setIsConflictOpen(false);
    const response = await toastAxios(
      () => newOperation({ ...pendingPayload, confirm_protocol_impact: true }, axiosPrivate),
      customIdNewOp,
      { name: operation?.type || "" },
      getLabel,
    );
    if (response?.success) {
      updateStore(response.updated_data);
      navClickHandler();
    }
  };

  const buildTreatments = (data, protocol, tmp_treatments_data) => {
    const getSubstanceById = (id) => drugs?.find((s) => s?.id === Number(id));
    // Build from protocol operations
    for (const step of (protocol?.operations || [])) {
      const tmp = {
        id: Number(step.id),
        day: Number(step.day),
        phase: step.phase,
        operation: step.operation,
        goal: step.goal,
        note: step.note,
        protocol_id: protocol?.id,
        info: {
          analysisType: data?.analysisType?.value || "tank",
        },
        // ...status,
      };

      const substance = getSubstanceById(step?.product);
      if (!substance) continue;

      const baseQuantity = Number(step.quantity) || 0;

      // Override quantity from data.column if present
      const override = Object.values(data.column || {}).find(
        (col) => Number(col.substance_id) === substance.id
      );
      const quantity =
        override && Number(override.new) >= 0
          ? Number(override.new)
          : baseQuantity;

      // const status = substanceStatusMap[substance.id] || { removed: false, added: false };
      // tmp.operation = "ADD";
      tmp.product = substance?.id;
      tmp.substance_name = substance?.name;
      tmp.quantity = Number(quantity);
      tmp.unit_id = substance.unit_id || substance?.unit_id;
      tmp.unit = substance.unit || substance?.unit;
      tmp.protocol_type =
        step.operation === "ADD" ? step?.functional : step.operation;

      tmp_treatments_data.push(tmp);
    }
  }

  const updateStore = (payloadOp) => {
    if (id && !isDuplicate) {
      dispatch(updateOp(payloadOp));
    } else {
      dispatch(add(payloadOp));
    }
  }

  const navClickHandler = () => {
    if (id) {
      navigate(`/programming/${type?.toLowerCase?.()}/${id}`);
    } else {
      navigate("/programming");
    }
  };

  const getError = (idx, data, key) => {
    if (
      !errors ||
      Object.keys(errors)?.length === 0 ||
      !data ||
      data?.length <= idx
    )
      return;

    return data?.[idx]?.[key] || null;
  };

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  const openFormModal = () => {
    // trigger(); // TO DO remove trigger from all

    const expDate = new Date(getValues("expire_date"));
    if (new Date(expDate?.setHours(23, 59, 59)) < new Date()) {
      setError("expire_date", {
        type: "manual",
        message: getLabel("errorDateInThePast"),
      });
      return;
    } else clearErrors("expire_date");

    if (dest_tanks?.filter((t) => t?.quantity)?.length === 0) {
      toast(getLabel("toast_destemmingMissingDests"), {
        toastId: customIdMissingDests,
        type: toast.TYPE.ERROR,
        isLoading: false,
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    } else {
      toast.dismiss(customIdMissingDests);
    }

    if (
      dest_tanks?.filter(
        (t) =>
          (!t?.batch_name_selector?.value &&
            t?.batch_name_selector?.label?.length === 0) ||
          (t?.batch_name_selector?.value === true &&
            t?.new_batch_name?.length === 0)
      )?.length > 0
    ) {
      toast(getLabel("toast_destemmingMissingLotSelectors"), {
        toastId: customIdMissingDests,
        type: toast.TYPE.ERROR,
        isLoading: false,
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    } else {
      toast.dismiss(customIdMissingDests);
    }
    const transferLiters = getTotalQuantity();

    const totalLitersAvailable = Number(getValues("tank_id_in")?.quantity);
    if (transferLiters <= totalLitersAvailable) {
      toast.dismiss(customIdInvalidQuantities);
      setIsOpen(true);
    } else {
      toast(
        getLabel("toast_bottlingInvalidQuantities", {
          sum: transferLiters || "0",
          total: totalLitersAvailable,
        }),
        {
          toastId: customIdInvalidQuantities,
          type: toast.TYPE.ERROR,
          isLoading: false,
          position: toast.POSITION.BOTTOM_RIGHT,
        }
      );
      
      return;
    }
  };

  const computeAddOpTypeString = (row) => {
    if (row?.treatments?.length > 1) {
      return getLabel(`operationType${row.type}`);
    }
    const protocol_type = row?.functional || null;
    const subtype = protocol_type
      ? getLabel(`drugLabelGroup_${protocol_type}`)
      : null;
    return `${getLabel(`protocolType_${row.operation}`)} ${
      subtype ? "(" + subtype?.toLowerCase?.() + ")" : ""
    }`;
  };

  const customIdMissingDests = "custom-id-missing-dests";
  const customIdInvalidLotName = "custom-id-invalid-lot-name";
  const customIdInvalidQuantities = "custom-id-invalid-quantities";
  const customIdInvalidBatchTypes = "custom-id-invalid-batch-types";
  const customId = "custom-id-different-color";
  const checkColors = () => {
    const colors = new Set();
    dest_tanks?.reduce(
      (_, item) =>
        item?.color ? colors.add(item?.color) : null,
      null
    );
    if (getValues("tank_id_in")?.color)
      colors.add(getValues("tank_id_in")?.color);

    if (colors?.size > 1)
      toast(getLabel("toast_WarningDiffentWineColor"), {
        toastId: customId,
        type: toast.TYPE.WARNING,
        isLoading: false,
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: false,
      });
    else toast.dismiss(customId);
  };

  const getTotalQuantity = () => {
    // Restituisce LITRI: i valori del form sono in unità input.
    return (
      dest_tanks?.reduce((acc, curr) => {
        return acc + inputToLiters(curr?.quantity, inputUnit);
      }, 0) || 0
    );
  };

  // const computeLotNames = (isValidate = false) => {
  //   const names = new Set();

  //   // shows only 6 months old must batches. TO DO: what if a batch of the previous year has the same name?? I hope it is deactivated
  //   // lots?.filter((l) => (l?.state === 'MUST' || l?.state === 'WINE') && (new Date(l?.insertion_date) >= addMonths(new Date(), -6)))?.reduce((_, l) => names.add(l?.name), null);
  //   lotNames
  //     ?.filter((l) => l?.state === "MUST" || l?.state === "WINE")
  //     ?.reduce((_, l) => names.add(l?.name), null);

  //   if (dests?.length === 1 && dests?.[0]?.value === null)
  //     return names;
  //   dests
  //     ?.filter((tank) => tank?.quantity > 0)
  //     ?.reduce((_, item) => names.add(item?.lot), null);

  //   if (!isValidate) {
  //     for (const tank of dests) {
  //       if (tank?.new_batch_name?.length > 0) names.add(tank?.new_batch_name);
  //     }
  //     let i = 0;
  //     for (const tank of dests?.filter(
  //       (t) => t?.value && "batch_name_selector" in t
  //     )) {
  //       if (
  //         tank?.batch_name_selector?.value === false &&
  //         !Array.from(names.values())?.includes(
  //           tank?.batch_name_selector?.label
  //         )
  //       ) {
  //         setValue(`dests.${i}.batch_name_selector`, {
  //           value: "",
  //           label: "",
  //         });
  //       }
  //       i += 1;
  //     }
  //   }

  //   const nameObjects = [];
  //   names?.forEach((s) => {
  //     if (s) nameObjects.push({ value: false, label: s });
  //   });

  //   return nameObjects?.sort((a, b) =>
  //     a?.label > b?.label ? 1 : b?.label > a?.label ? -1 : 0
  //   );
  // };

  const getTankData = (t, isDetail = false) => {
    const tank = t; //!isDetail ? t?.tankData : t;
    const quantity = !isDetail
      ? tank?.availability >= Number(t?.quantity) && Number(t?.quantity) >= 0
        ? Number(t?.quantity)
        : 0
      : getTotalQuantity();
    const new_batch_name = !isDetail
      ? t?.batch_name_selector?.value === true && t?.new_batch_name
        ? t.new_batch_name
        : t?.batch_name_selector?.value === false
        ? t?.batch_name_selector?.label
        : ""
      : tank?.lot;

    const tankData = {};
    tankData.name = t?.label;
    tankData.is_src = isDetail;
    tankData.arrow_quantity = getTotalQuantity();
    tankData.capacity = tank.capacity;
    tankData.old_quantity = tank.quantity;
    tankData.old_batch_name = tank.lot || "";
    tankData.quantity = tank.quantity - tankData.arrow_quantity;
    tankData.color =
      tank.quantity > 0 ? tank?.color : getValues("batch_id_in")?.color;
    // tankData.batch_name = tank.quantity - tankData.arrow_quantity <= 0 ? " " : tankData?.old_batch_name;

    tankData.is_planned = tank?.is_planned;
    tankData.is_planned_state = tank?.is_planned_state;
    tankData.planned_quantity = tank?.planned_quantity;
    tankData.planned_batch_name = tank?.planned_batch_name;
    tankData.planned_batch_type = tank?.planned_batch_type;
    if (!tank?.is_planned) {
      tankData.planned_quantity = tank?.quantity;
    }
    
    tankData.color =
      tank.quantity > 0 ? tank?.color : getValues("tank_id_in")?.color;
    tankData.batch_name = new_batch_name;

    if (tankData?.quantity < 0 && isDetail) {
      tankData.quantity = 0;
      tankData.arrow_quantity = -Math.abs(tankData?.old_quantity);
    }

    if (Number(tank?.quantity) < Math.abs(Number(tankData?.arrow_quantity))) {    // in case of excessive arrow_quantity
      tankData.quantity = tank?.quantity;
      tankData.planned_quantity = tank?.quantity;
      tankData.arrow_quantity = 0;
    }

    return tankData;
  };

  const filterBottleCapData = (data, idx, is_bottle = true) => {
    if (!data?.length) return [];
  
    const key = is_bottle ? 'bottle_lot' : 'cap_lot';
    const selectedValue = dest_tanks?.[idx]?.[key]?.value;
    const showAll = !selectedValue;
  
    return [
      { label: "", value: "" }, // Dummy empty option
      ...data.map((item) => {
        const itemId = item?.id;
        const currentQuantity = Number(dest_tanks?.[idx]?.quantity);
        // Calculate how much of this item is used across all dests (excluding current idx)
        const totalUsedQty = dest_tanks?.reduce((acc, dest, i) => {
          const destQty = Number(dest?.quantity) || 0;
          const destVal = dest?.[key]?.value;
          // Include all uses of this item except at current index
          if (destVal === itemId && i !== idx) {
            return acc + destQty;
          }
          return acc;
        }, 0) || 0;
  

        // Filter out the currently selected item at idx
        if (itemId === selectedValue) return null;
  
        const totalQtyUsedIncludingCurrent = totalUsedQty + (Number(currentQuantity) || 0);
        const requiredItems = Math.ceil(totalQtyUsedIncludingCurrent / 0.75);
        const availableItems = Number(item?.quantity) || 0;
      
        // Disable the item if the required bottles exceed the remaining stock
        const isDisabled = requiredItems > availableItems;
        return {
          value: itemId,
          label: item?.name,
          quantity: Number(item?.quantity),
          name: `${item?.name} (x${item?.quantity || 0})`,
          is_disabled: isDisabled,
        };
      }).filter(item => item !== null), // Remove nulls from the list
    ];
  };

  const getDrugData = (substance_id) => {
    const drug = drugs?.find(d => d?.id === substance_id);
    if (drug) return { recommended_quantity: drug?.recommended_quantity, stock: drug?.stock || 0, batches: drug?.batches || [] }
    return { recommended_quantity: 0, stock: 0, batches: [] }
  }

  const buildWinemakingBottlingTreatments = () => {
    const bottlingProtocols = protocols
      ?.filter((p) => p?.protocol_type === 'POST' && p?.operations?.some(op => op?.phase === 'BOTTLING'))
      ?.map?.((p) => {
      return {
        ...p,
        value: p?.id,
        label: p?.name,
      }
    }) || [];
    
    const winemakingTreatments = [];
    for (const p of bottlingProtocols) {
      const t = buildWinemakingTreatment(p);
      if (t) winemakingTreatments.push(t);
    }
// console.log('bottlingProtocols', bottlingProtocols, 'winemakingTreatments', winemakingTreatments)
    
    return winemakingTreatments;
  }
  
  const buildWinemakingTreatment = (protocol) => {
    if (!protocol) return {};
    // const {
    //   color = "RED",
    //   state = "WINE",
    //   notes = "",
    // } = options;

    const ops = protocol?.operations || [];
    const drugs = ops.map(op => ({
      phase: op?.phase,
      type: op.product_type,               // ADDITIVE | ADJUVANT
      unit: op.product_unit,                // g/hL | mg/L
      quantity: Number(op.quantity) || 0,
      min_range: op.product_min_range,
      max_range: op.product_max_range,
      substance_id: op.product,
      substance_name: op.product_name,

      // values from current drugs status
      ...getDrugData(op.product),

      // opzionali / dominio
      // min_temperature: null,
      // max_temperature: null,
      // recommended_quantity: Number(op.quantity) || 0,
    }));

    return {
      type: "TREATMENT",
      // color,
      // state,
      // notes,

      // client_name: protocol.client_name,

      protocols_id: protocol.id,
      protocols_name: protocol.name,
      protocols_subtype: "PRODUCT",

      value: protocol.id,
      label: protocol.name,
      name: protocol.name,

      drugs,

      // array derivati (comodi per submit)
      quantities: drugs.map(d => d.quantity),
      substance_ids: drugs.map(d => d.substance_id),
    };
  }

  const winemakingBottlingTreatments = useMemo(
    () => buildWinemakingBottlingTreatments(),
    [protocols, drugs]
  );

  return (
    <div className="operation_form_wrapper">
      <form
        onSubmit={handleSubmit(openFormModal, (errors) => console.log("Form validation errors:", errors))}
        autoComplete="off"
        noValidate
      >
        {/* <h6>{getLabel("fromTank")}</h6> */}
        {!forcedSrcTank && (
          <SourceSelector
            id={id}
            type={type}
            configs={configs}
            subtype={subtype?.value}
            operation={operation}
            control={control}
            errors={errors}
            tanks={tanks}
            lots={lots}
            tank_id_in={tank_id_in}
            dest_tanks={dest_tanks}
            getValues={getValues}
            setValue={setValue}
            // getTankData={getTankData}
            isMobile={isMobile}
          />
        )}
        {forcedSrcTank && tank_id_in?.value && (
          <div className="operation_form_wrapper_row">
            <TankIcon
              tank={tank_id_in}
              isText={true}
              isDestemming={false}
              isSrc={true}
              isRecap={false}
            />
          </div>
        )}
        <DestinationSelector
          id={id}
          type={type}
          subtype={subtype?.value}
          operation={operation}
          register={register}
          control={control}
          errors={errors}
          clearErrors={clearErrors}
          setError={setError}
          setValue={setValue}
          getValues={getValues}
          tanks={tanks}
          lots={lots}
          tank_id_in={tank_id_in}
          fields={fields}
          lotNames={lotNames}
          // dest_tanks={dest_tanks}
          append={append}
          update={update}
          remove={remove}
          // getTankData={getTankData}
          // lotTypeMap={lotTypeMap}
          // setLotTypeMap={setLotTypeMap}
          isMobile={isMobile}
          watchedArray={dest_tanks}
          capsData={capsData}
          bottlesData={bottlesData}
          isWizardMode={!!onConfigSave}
        />
        
        <h6>{getLabel("protocolBottlingMachineTitle")}</h6>
        <Select
          label={getLabel("protocolBottlingMachine")}
          placeholder={getLabel("protocolBottlingMachinePlaceholder")}
          tooltip={getLabel("protocolBottlingTooltip")}
          errors={errors[`machinery_id`]}
          name={`machinery_id`}
          control={control}
          options={machinesData?.filter(m => m?.type === "BOTTLING_MACHINE")?.map?.((p) => {
            return {
              value: p?.id,
              label: p?.name,
            };
          })}
        />
        
        <h6>{getLabel("protocolBottlingCleaningTitle")}</h6>
        <div className="operation_form_wrapper_row">
          <Select
            label={getLabel("protocolBottlingCleaning")}
            placeholder={getLabel("protocolBottlingCleaningPlaceholder")}
            tooltip={getLabel("protocolBottlingCleaningTooltip")}
            errors={errors[`cleaning_protocol_id`]}
            name={`cleaning_protocol_id`}
            isDisabled={!machinery_id?.value}
            control={control}
            options={(protocols?.filter((p) => p?.protocol_type === 'CIP' && p?.protocol_subtype === 'CLEANING_MACHINES') ?? [])?.map?.((p) => {
              return {
                value: p?.id,
                label: p?.name,
              };
            })}
          />
        </div>
        {getValues("cleaning_protocol_id")?.value && (
        <div className="operation_form_wrapper_row vertical">
          <TableEdit
            defaultData={
              protocols?.find(p => p?.id === getValues("cleaning_protocol_id")?.value)
              ?.operations?.map((s) => {
                return {
                  ...s,
                  opType: "WINEMAKINGPROTOCOL",
                };
              }) || []
            }
            isLegend={false}
            isSorting={false}
            getValues={getValues}
            // setRows={setOperationsP}
            columns={cipColumns}
            // isEditable={true}
            isSearchBar={false}
            // errors={modalErrors}
            // control={modalControl}
            // setValue={modalSetValue}
            // getValues={modalGetValues}
            type={"winemaking"}
            // modalHandler={handleOpenModal}
            // setModifyMode={setModifyMode}
            // newRow={{
            //   substance_name: "",
            //   min_range: "",
            //   max_range: "",
            //   min_temperature: "",
            //   max_temperature: "",
            //   unit: "",
            //   quantity: "",
            //   type: "",
            // }}
          />
        </div>)}
        {/* <h6>{getLabel("protocolBottlingTitle")}</h6>
        <div className="operation_form_wrapper_row">
          <Select
            label={getLabel("protocolBottling")}
            placeholder={getLabel("protocolBottlingPlaceholder")}
            tooltip={getLabel("protocolBottlingTooltip")}
            errors={errors[`protocol_id`]}
            isDisabled={!machinery_id?.value}
            name={`protocol_id`}
            control={control}
            options={protocols?.filter((p) => p?.protocol_type === 'POST' && p?.operations?.some(op => op?.phase === 'BOTTLING'))?.map?.((p) => {
              return {
                value: p?.id,
                label: p?.name,
              };
            })}
          />
        </div> */}
        
        <DrugsBatchSelectorTable
          id={id}
          type={type}
          title={"protocolBottlingTitle"}
          isSelectDisabled={!machinery_id?.value ?? false}
          operation={operation}
          protocol_id={protocol_id}
          configs={configs}
          protocols={winemakingBottlingTreatments}
          drugs={drugs}
          units={units}
          reset={reset}
          errors={errors}
          control={control}
          register={register}
          setValue={setValue}
          getValues={getValues}
          totalTreatmentVolume={totalTreatmentVolume}
          isBatchSelectorModal={isBatchSelectorModal}
          drugBatchFieldArray={drugBatchFieldArray}
          getLabel={getLabel}
        />

        <h6>{getLabel("operationAdditional")}</h6>
        <div className="operation_form_wrapper_row">
          <MultipleSelect
            name={`cellarman_ids`}
            control={control}
            label={getLabel("operationUser")}
            placeholder={getLabel("operationUserPlaceholder")}
            error={errors["cellarman_ids"]}
            required={getLabel("inputRequiredError")}
            options={users?.map?.((user) => {
              return {
                value: user?.id,
                label: user?.username,
              };
            })}
            tooltip={getLabel("destemmingCellarmenTooltip")}
          />
          <Select
            name="priority"
            control={control}
            label={getLabel("operationPriority")}
            placeholder={getLabel("operationPriorityPlaceholder")}
            error={errors}
            required={getLabel("inputRequiredError")}
            options={[
              {
                label: getLabel("lotQuality_HIGH"),
                value: "2",
              },
              {
                label: getLabel("lotQuality_MEDIUM"),
                value: "1",
              },
              {
                label: getLabel("lotQuality_LOW"),
                value: "0",
              },
            ]}
            // defaultValue={{
            //     value: "",
            //     label: "",
            //   }}
          />
          <DatePicker
            name="expire_date"
            control={control}
            label={getLabel("operationExpireDateForm")}
            error={errors["expire_date"]}
            required={getLabel("inputRequiredError")}
            dateFormat={configs.shortDateFormat}
            placeholder={getLabel("expiringDatePlaceholder")}
          />
          {getValues("expire_date") && <Input
            name={"expire_date_exact"}
            register={register}
            label={getLabel("isExactDate")}
            error={errors["expire_date_exact"]}
            // isLabel={false}
            type={"checkbox"}
            checked={isExactDate}
            onChange={() => setIsExactDate((prev) => !prev)}
            customClassName={"confirm no_margin_top"}
            tooltip={getLabel("destemmingExactDate")}
          />}
        </div>

        <div className="operation_form_wrapper_row">
          <TextArea
            name="note"
            register={register}
            label={getLabel("clientNotes")}
            placeholder={getLabel("clientNotesPlaceholder")}
            error={errors["note"]}
          />
        </div>
        {!onConfigSave && (
          <div className="operation_form_wrapper_row">
            <Submit label={getLabel("submitForm")}></Submit>
          </div>
        )}
      </form>
      {isBatchSelectorModal !== "BATCH_SELECTOR" && (
      <ConfirmationModal
        isOpen={isOpen}
        onConfirm={sendData}
        onClose={handleCloseModal}
        description={getLabel("modalNewBottlingDescription")}
        isOperationRecap={true}
        totalStep={0}
        errors={errors}
        getValues={getValues}
        // setNewBatchName={setNewBatchName}
      >
        <OperationRecap
          type={type}
          dest_tanks={dest_tanks?.map((d, index) => {
            return {
              ...d,
              name: d?.label,
              color: d?.color || tank_id_in?.color,
              add_quantity: inputToLiters(d?.quantity, inputUnit),
              batch_type: d?.batch_type || tank_id_in?.batch_type,
              batch_name:
                d?.batch_name_selector?.value === true
                  ? d?.new_batch_name
                  : d?.batch_name_selector?.label,
            };
          })}
          src_tanks={[getTankData(tank_id_in)]}
        />
      </ConfirmationModal>)}
      <ProtocolConflictModal
        isOpen={isConflictOpen}
        protocols={conflictProtocols}
        onConfirm={handleConflictConfirm}
        onCancel={() => setIsConflictOpen(false)}
      />
    </div>
  );
});

BottlingForm.displayName = "BottlingForm";

export default BottlingForm;
